import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../../../firebase/config";
import { Entry } from "../../../types";
import { formatEventScore, parseEventScore } from "../../../utils/scoreUtils";

export default function EntryTable() {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [currentlyEditing, setCurrentlyEditing] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Entry>>({});
  const [currentDocId, setCurrentDocId] = useState<string | null>(null);

  useEffect(() => {
    const fetchEntries = async () => {
      const snapshot = await getDocs(collection(db, "entries"));
      const data = snapshot.docs.map((docSnap) => ({
        ...(docSnap.data() as Entry),
        docId: docSnap.id,
      }));
      setEntries(data);
    };
    fetchEntries();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (entry: Entry) => {
    const docId = entry.docId;
    setCurrentlyEditing(docId ?? null);
    setCurrentDocId(docId ?? null);
    setEditForm(entry);
  };

  const handleSave = async () => {
    if (
      !editForm.email ||
      !editForm.event ||
      !editForm.result ||
      !editForm.year ||
      !editForm.month ||
      !editForm.day ||
      !currentDocId
    )
      return;

    const updatedScore = parseEventScore(
      editForm.event,
      editForm.result.toString()
    );

    const updatedEntry: Entry = {
      ...editForm,
      score: updatedScore,
    } as Entry;

    await setDoc(doc(db, "entries", currentDocId), updatedEntry);

    setEntries((prev) =>
      prev.map((e) => (e.docId === currentDocId ? updatedEntry : e))
    );
    setCurrentlyEditing(null);
    setEditForm({});
    setCurrentDocId(null);
  };

  const handleCancel = () => {
    setCurrentlyEditing(null);
    setEditForm({});
  };

  const handleDelete = async (entry: Entry) => {
    const docId = entry.docId;
    if (docId) {
      await deleteDoc(doc(db, "entries", docId));
    } else {
      console.error("Document ID is undefined");
    }
    setEntries((prev) => prev.filter((e) => e.docId !== docId));
  };

  return (
    <table className="admin-data-table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Event</th>
          <th>Athlete</th>
          <th>Gender</th>
          <th>Age</th>
          <th>Result</th>
          <th>Score</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, idx) => {
          const isEditing = currentlyEditing === entry.docId;
          return (
            <tr key={idx}>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    name="date"
                    className="admin-edit-input"
                    value={
                      editForm.year && editForm.month && editForm.day
                        ? `${editForm.year}-${String(editForm.month).padStart(
                            2,
                            "0"
                          )}-${String(editForm.day).padStart(2, "0")}`
                        : ""
                    }
                    onChange={(e) => {
                      const [y, m, d] = e.target.value.split("-").map(Number);
                      setEditForm((prev) => ({
                        ...prev,
                        year: y,
                        month: m,
                        day: d,
                      }));
                    }}
                  />
                ) : (
                  `${entry.month}/${entry.day}/${entry.year}`
                )}
              </td>
              <td>
                {isEditing ? (
                  <select
                    name="event"
                    value={editForm.event}
                    className="admin-edit-input"
                    onChange={handleChange}
                  >
                    <option value="bench">bench</option>
                    <option value="squat">squat</option>
                    <option value="shots">shots</option>
                    <option value="swim">swim</option>
                    <option value="run">run</option>
                  </select>
                ) : (
                  entry.event
                )}
              </td>
              <td>
                {entry.firstName} {entry.lastName}
              </td>
              <td>{entry.gender}</td>
              <td>{entry.age}</td>
              <td>
                {isEditing ? (
                  <input
                    name="result"
                    className="admin-edit-input"
                    value={editForm.result}
                    onChange={handleChange}
                  />
                ) : (
                  entry.result
                )}
              </td>
              <td>{entry.score}</td>
              <td>
                {isEditing ? (
                  <>
                    <button onClick={handleSave}>Save</button>
                    <button onClick={handleCancel}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleEdit(entry)}>Edit</button>
                    <button onClick={() => handleDelete(entry)}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
